import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, of } from 'rxjs';
import { ApiService } from 'src/app/core/services/api.service';
import {
  CreateOnboardingAnswerRequest,
  LoginApiError,
  LoginApiResponse,
  LoginConfirmResponse,
  LoginSendCodeResponse,
  LoginSessionResponse,
  OnboardingCreateAnswerResponse,
  OnboardingQuestionsResponse,
  PvzEmployeesResponse,
  PvzPincodeRequest,
} from '../models/login-v2-api.models';

@Injectable({
  providedIn: 'root',
})
export class LoginV2ApiService {
  constructor(private readonly api: ApiService) {}

  sendLoginCode(login: string): Observable<LoginSendCodeResponse> {
    return this.api.post('v1/login', { login }, true);
  }

  sendRegisterCode(login: string): Observable<LoginSendCodeResponse> {
    return this.api.post_new('v1/register', { login }, true).pipe(
      catchError((error: HttpErrorResponse) => {
        const body = (error?.error ?? {}) as Partial<LoginApiError>;
        return of({
          success: false,
          message: this.getErrorMessage(error, body.message),
        } as LoginSendCodeResponse);
      }),
    );
  }

  confirmLoginCode(login: string, code: string): Observable<LoginConfirmResponse> {
    return this.confirmCode('v1/login', login, code);
  }

  confirmRegisterCode(login: string, code: string): Observable<LoginSessionResponse> {
    return this.confirmCode('v1/register', login, code);
  }

  selectAccount(
    login: string,
    ownerId: number,
    code: string,
  ): Observable<LoginSessionResponse> {
    return this.api.post(
      'v1/login',
      { login, owner: ownerId, code },
      true,
    );
  }

  confirmPvzPincode(params: PvzPincodeRequest): Observable<LoginSessionResponse> {
    return this.api.post_new('v1/login/pvz', params, true).pipe(
      catchError((error: HttpErrorResponse) => {
        const body = (error?.error ?? {}) as Partial<LoginApiError>;
        return of({
          success: false,
          message: this.getErrorMessage(error, body.message),
          login_attempts: body.login_attempts,
        } as LoginSessionResponse);
      }),
    );
  }

  getPvzEmployees(pvzUuid: string): Observable<PvzEmployeesResponse> {
    return this.api.get(`v1/login/pvz/${pvzUuid}`);
  }

  private confirmCode<T extends LoginApiResponse>(endpoint: string, login: string, code: string): Observable<T> {
    return this.api.post_new(endpoint, { code, login }, true).pipe(
      catchError((error: HttpErrorResponse) => {
        const body = (error?.error ?? {}) as Partial<T> & {
          login_attempts?: number;
        };
        return of({
          success: false,
          message: this.getErrorMessage(error, body.message),
          login_attempts: body.login_attempts,
        } as unknown as T);
      }),
    );
  }

  private getErrorMessage(
    error: HttpErrorResponse,
    bodyMessage?: string,
  ): string | undefined {
    if (bodyMessage) {
      return bodyMessage;
    }
    if (error.status === 0 || error.status === 504) {
      return 'Проверьте подключение к Интернету и повторите попытку.';
    }
    if (error.status === 429) {
      return 'Превышен лимит запросов. Повторите попытку через 60 секунд.';
    }
    if (error.status >= 500) {
      return (
        'На сервере ведутся технические работы! ' +
        'Повторите попытку позже. Если проблема будет повторяться, ' +
        'обратитесь в поддержку.'
      );
    }
    return undefined;
  }

  getOnboardingQuestions(): Observable<OnboardingQuestionsResponse> {
    return this.api.get('v2/users/onboarding/questions');
  }

  createOnboardingAnswer(questionId: number, body: CreateOnboardingAnswerRequest): Observable<OnboardingCreateAnswerResponse> {
    return this.api.post(`v2/users/onboarding/create_answer/${questionId}`, body, true);
  }

  checkTel(token: string, tel: string): Observable<LoginApiResponse> {
    this.api.setToken(token);
    return this.api.post('v1/user/tel/check', { tel }, true).pipe(
      catchError((error: HttpErrorResponse) => {
        const body = (error?.error ?? {}) as Partial<LoginApiResponse>;
        return of({
          success: false,
          message: body.message,
        } as LoginApiResponse);
      }),
    );
  }

  setSessionToken(token: string): void {
    this.api.setToken(token);
  }

  /**
   * Показывает индикатор загрузки.
   */
  presentLoading(keyboardClose = true): Promise<void> {
    return this.api.loadingPresent(keyboardClose);
  }

  /**
   * Скрывает индикатор загрузки.
   */
  dismissLoading(): void {
    this.api.loadingDismiss();
  }

  toast(message: string, severity: 'success' | 'info' | 'error' | 'warn' | 'secondary' | 'contrast' = 'info', summary?: string): void {
    this.api.toastPresent(message, true, summary, severity);
  }
}
